import React, { useState, useMemo, useEffect } from 'react'
import { Search, Loader2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { getPayrollReport } from '@/services/payrollService'
import type { PayrollReportItem } from '@/api/types/payroll.types'
import { getLogoPath } from '@/components/shared/Logo'
import { formatCurrency } from '@/lib/format'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function formatMonthYearLabel(ym?: string | null): string {
  if (!ym || ym.length < 6) return '—'
  const parts = String(ym).split('-')
  let monthNum: number
  let yearStr: string
  if (parts.length >= 2 && parts[0].length === 4) {
    yearStr = parts[0]
    monthNum = parseInt(parts[1], 10)
  } else if (parts.length >= 2 && parts[1].length === 4) {
    monthNum = parseInt(parts[0], 10)
    yearStr = parts[1]
  } else {
    return ym
  }
  if (monthNum >= 1 && monthNum <= 12) return `${MONTH_NAMES[monthNum - 1]} ${yearStr}`
  return ym
}

function formatNum(n?: number | null): string {
  if (n == null) return '—'
  return formatCurrency(Number(n))
}

export const PayrollReport: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [fromMonth, setFromMonth] = useState(getDefaultMonthYear)
  const [toMonth, setToMonth] = useState(getDefaultMonthYear)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<PayrollReportItem[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [printLoading, setPrintLoading] = useState(false)

  useEffect(() => {
    if (branchId) loadReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only when branchId is ready
  }, [branchId])

  const loadReport = async () => {
    if (!branchId) return
    setLoading(true)
    try {
      const res = await getPayrollReport(branchId, fromMonth, toMonth, pageNumber, pageSize)
      setList(res.data ?? [])
      setTotalRecords(res.totalRecords ?? 0)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to load report.')
      setList([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }

  const filteredList = useMemo(() => {
    if (!search.trim()) return list
    const q = search.toLowerCase()
    return list.filter(
      (r) =>
        (r.employeeName ?? '').toLowerCase().includes(q) ||
        (r.empCode ?? '').toLowerCase().includes(q) ||
        (r.department ?? '').toLowerCase().includes(q) ||
        (r.project ?? '').toLowerCase().includes(q)
    )
  }, [list, search])

  const handlePrint = async () => {
    if (!branchId) return
    setPrintLoading(true)
    try {
      const res = await getPayrollReport(branchId, fromMonth, toMonth, 1, 10000)
      const data = res.data ?? []
      const branchName = user?.selectedBranch?.branchName ?? 'Branch'
      const dateStr = new Date().toLocaleDateString('en-US', { month: 'numeric', day: '2-digit', year: '2-digit' })
      const timeStr = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      const fromLabel = formatMonthYearLabel(fromMonth)
      const toLabel = formatMonthYearLabel(toMonth)
      const primaryColor = 'hsl(225, 46%, 16%)'
      const thStyle = `background:${primaryColor};color:white;padding:6px 8px;text-align:left;font-size:11px;font-weight:600;border:1px solid #e5e7eb;`
      const tdStyle = 'padding:6px 8px;font-size:11px;border:1px solid #e5e7eb;'
      const num = (v: any) =>
        typeof v === 'number' ? formatCurrency(v) : v === '—' ? '—' : String(v ?? '—')
      const rows = data
        .map(
          (row: PayrollReportItem) => `
        <tr>
          <td style="${tdStyle}">${row.empCode ?? `EMP-${row.empId ?? ''}`}</td>
          <td style="${tdStyle}">${row.employeeName ?? '—'}</td>
          <td style="${tdStyle}">${row.project ?? '—'}</td>
          <td style="${tdStyle}">${row.department ?? '—'}</td>
          <td style="${tdStyle}">${num(row.grossSalary)}</td>
          <td style="${tdStyle}">${num(row.basicSalary)}</td>
          <td style="${tdStyle}">${num(row.housingAllowance)}</td>
          <td style="${tdStyle}">${num(row.transportationAllowance)}</td>
          <td style="${tdStyle}">${num(row.mealAllowance)}</td>
          <td style="${tdStyle}">${num(row.otherAllowance)}</td>
          <td style="${tdStyle}">${num(row.totalAllowance)}</td>
          <td style="${tdStyle}">${num(row.bonus)}</td>
          <td style="${tdStyle}">${num(row.incentive)}</td>
          <td style="${tdStyle}">${num(row.otAmount)}</td>
          <td style="${tdStyle}">${num(row.gosi)}</td>
          <td style="${tdStyle}">${num(row.absentDeduction)}</td>
          <td style="${tdStyle}">${num(row.lateDeduction)}</td>
          <td style="${tdStyle}">${num(row.earlyOutDeduction)}</td>
          <td style="${tdStyle}">${num(row.totalDeduction)}</td>
          <td style="${tdStyle}">${num(row.netSalary)}</td>
        </tr>`
        )
        .join('')
      const win = window.open('', '_blank')
      if (!win) {
        toast.error('Please allow popups to print.')
        return
      }
      win.document.write(`
        <!DOCTYPE html><html><head>
          <title>Payroll Report</title>
          <style>
            body { font-family: system-ui, sans-serif; padding: 12px; color: #1f2937; margin: 0; font-size: 12px; }
            table { border-collapse: collapse; width: 100%; }
            .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
            .header-date { font-size: 11px; color: #6b7280; }
            .header-branch { flex: 1; text-align: center; font-weight: 600; font-size: 14px; }
            .header-logo img { height: 32px; object-fit: contain; }
            .report-title { text-align: center; font-size: 18px; font-weight: 700; margin: 12px 0 8px; }
            .header-line { height: 3px; background: ${primaryColor}; margin: 8px 0 16px; }
            @media print { body { padding: 0; } }
          </style>
        </head><body>
          <div class="header-row">
            <div class="header-date">${dateStr}, ${timeStr}</div>
            <div class="header-branch">${branchName}</div>
            <div class="header-logo"><img src="${window.location.origin}${getLogoPath()}" alt="Logo" /></div>
          </div>
          <div class="report-title">Payroll Report</div>
          <div style="text-align:center;font-size:11px;margin-bottom:12px;">Payroll Report from ${fromLabel} to ${toLabel}</div>
          <div class="header-line"></div>
          <table>
            <thead>
              <tr>
                <th style="${thStyle}">EMP CODE</th>
                <th style="${thStyle}">EMPLOYEE</th>
                <th style="${thStyle}">PROJECT</th>
                <th style="${thStyle}">DEPARTMENT</th>
                <th style="${thStyle}">GROSS SALARY</th>
                <th style="${thStyle}">BASIC</th>
                <th style="${thStyle}">HOUSING</th>
                <th style="${thStyle}">TRANSPORT</th>
                <th style="${thStyle}">MEAL</th>
                <th style="${thStyle}">OTHER EARNINGS</th>
                <th style="${thStyle}">TOTAL ALL.</th>
                <th style="${thStyle}">BONUS</th>
                <th style="${thStyle}">INCENTIVE</th>
                <th style="${thStyle}">OT AMOUNT</th>
                <th style="${thStyle}">GOSI</th>
                <th style="${thStyle}">UNPAID LEAVE / ABSENCE</th>
                <th style="${thStyle}">LATE DED.</th>
                <th style="${thStyle}">EARLY OUT DED.</th>
                <th style="${thStyle}">TOTAL DED.</th>
                <th style="${thStyle}">NET SALARY</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body></html>
      `)
      win.document.close()
      win.focus()
      setTimeout(() => {
        win.print()
        win.close()
      }, 300)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to load data for print.')
    } finally {
      setPrintLoading(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('navigation.payrollReport')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">From Month</label>
              <Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className="w-full" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">To Month</label>
              <Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} className="w-full" />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="default" size="sm" onClick={loadReport} disabled={loading || !branchId}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Search
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} disabled={printLoading || !branchId}>
                {printLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                Print
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search in list" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
          </div>

          <div className="rounded-md border overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center">Emp Code</TableHead>
                    <TableHead className="text-center">Employee</TableHead>
                    <TableHead className="text-center">Project</TableHead>
                    <TableHead className="text-center">Department</TableHead>
                    <TableHead className="text-center">Gross Salary</TableHead>
                    <TableHead className="text-center">Basic Salary</TableHead>
                    <TableHead className="text-center">Housing</TableHead>
                    <TableHead className="text-center">Transport</TableHead>
                    <TableHead className="text-center">Meal</TableHead>
                    <TableHead className="text-center">Other Earnings</TableHead>
                    <TableHead className="text-center">Total Allowance</TableHead>
                    <TableHead className="text-center">Bonus</TableHead>
                    <TableHead className="text-center">Incentive</TableHead>
                    <TableHead className="text-center">OT Amount</TableHead>
                    <TableHead className="text-center">GOSI</TableHead>
                    <TableHead className="text-center">Unpaid Leave / Absence</TableHead>
                    <TableHead className="text-center">Late Deduction</TableHead>
                    <TableHead className="text-center">Early Out Deduction</TableHead>
                    <TableHead className="text-center">Total Deduction</TableHead>
                    <TableHead className="text-center">Net Salary</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredList.length === 0 ? (
                    <TableRow>
                    <TableCell colSpan={20} className="text-center text-muted-foreground py-8">
                        No data. Select From/To Month and click Search to load report.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredList.map((row, idx) => (
                      <TableRow key={row.empId ?? idx}>
                        <TableCell className="text-center">{row.empCode ?? `EMP-${row.empId ?? ''}`}</TableCell>
                        <TableCell className="text-center">{row.employeeName ?? '—'}</TableCell>
                        <TableCell className="text-center">{row.project ?? '—'}</TableCell>
                        <TableCell className="text-center">{row.department ?? '—'}</TableCell>
                        <TableCell className="text-center">{formatNum(row.grossSalary)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.basicSalary)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.housingAllowance)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.transportationAllowance)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.mealAllowance)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.otherAllowance)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.totalAllowance)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.bonus)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.incentive)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.otAmount)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.gosi)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.absentDeduction)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.lateDeduction)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.earlyOutDeduction)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.totalDeduction)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.netSalary)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          {totalRecords > 0 && (
            <p className="text-sm text-muted-foreground">
              Showing {filteredList.length} of {totalRecords} entries.
            </p>
          )}
        </CardContent>
      </Card>
    </>
  )
}
